import { Box, Card, CardContent, Collapse, Skeleton, Tooltip, Typography } from "@mui/material";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { EnqueueSnackbar } from "notistack";
import AlertMessages from "common/Alert/AlertMessages";
import { Controller, useForm } from "react-hook-form";
import DynamicSelect from "common/Select/Select";
import { GetMonthlyPurchaseOrdersResponse } from "subModule/src/services/analytics/interface";
import { useGetMonthlyPurchaseOrdersService } from "subModule/src/services/analytics/useAnalytics";
import { InfiniteData } from "@tanstack/react-query";
import {
  dashboardWidgetHeaderSx,
  dashboardWidgetFiltersSx,
  dashboardWidgetTitleRowSx,
} from "components/Analytics/dashboardWidgetLayout";

interface MonthlyPurchaseOrdersProps {
    themes: any;
    stores: { value: number; label: string }[];
    enqueueSnackbar: EnqueueSnackbar;
    storesLoaded: boolean;
    handleDropdownActions: (typeName: string, action: 'searchIt' | 'loadNext', search?: string) => void;
    setDropdownPage: any;
    storesData: InfiniteData<any, unknown> | undefined;
    searchLoading: string | null;
    dropdownPage: number;
    dropdownSearchTerm: any;
    setDropdownSearchTerm: any;
    globalFilters: { storeId: any; allStore?: boolean; range: any };
    globalFilterApplied: boolean;
    setGlobalFilterApplied: (value: boolean) => void;
}

type MonthlyPurchaseOrdersData = Pick<GetMonthlyPurchaseOrdersResponse, 'title' | 'totalPurchaseOrders' | 'suppliers'>;
type SupplierItem = MonthlyPurchaseOrdersData['suppliers'][number];
type StatusBreakdown = SupplierItem['statusBreakdown'];

const STATUS_COLORS = {
    received: '#16A34A',
    billed: '#2563EB',
    inTransit: '#D97706',
} as const;

const STATUS_SEGMENTS: { key: keyof StatusBreakdown; labelKey: string; color: string }[] = [
    { key: 'received', labelKey: 'Received', color: STATUS_COLORS.received },
    { key: 'billed', labelKey: 'billed', color: STATUS_COLORS.billed },
    { key: 'inTransit', labelKey: 'inTransit', color: STATUS_COLORS.inTransit },
];

const formatPoDate = (date: string) => {
    if (!date) return date;
    return date.split('T')[0].split(' ')[0];
};

const getPoDetailStatusStyle = (status: string) => {
    switch (status) {
        case 'Approved':
            return { backgroundColor: '#E8F4FD', color: '#2563EB' };
        case 'Received':
            return { backgroundColor: '#D1E5DB', color: '#16A34A' };
        case 'Bill':
        case 'Billed':
            return { backgroundColor: '#E8F4FD', color: '#2563EB' };
        case 'InTransit':
        case 'In Transit':
            return { backgroundColor: '#FFF7ED', color: '#D97706' };
        default:
            return { backgroundColor: '#F5F5F5', color: '#636363' };
    }
};

const formatStatusBreakdown = (breakdown: StatusBreakdown, t: (key: string) => string) =>
    STATUS_SEGMENTS
        .filter(({ key }) => breakdown[key] > 0)
        .map(({ key, labelKey }) => `${breakdown[key]} ${t(labelKey).toLowerCase()}`)
        .join('  ');

const getActivePoCount = (breakdown: StatusBreakdown) =>
    STATUS_SEGMENTS.reduce((sum, { key }) => sum + (breakdown[key] ?? 0), 0);

const StatusProgressBar = ({ breakdown, total }: { breakdown: StatusBreakdown; total: number }) => {
    if (!total) {
        return <Box sx={{ height: 8, borderRadius: '4px', backgroundColor: '#F3F4F6' }} />;
    }
    return (
        <Box className="flex w-full overflow-hidden" sx={{ height: 8, borderRadius: '4px', backgroundColor: '#F3F4F6' }}>
            {STATUS_SEGMENTS.map(({ key, color }) => {
                const count = breakdown[key];
                if (!count) return null;
                return (
                    <Box
                        key={key}
                        sx={{
                            width: `${(count / total) * 100}%`,
                            backgroundColor: color,
                            minWidth: count > 0 ? '4px' : 0,
                        }}
                    />
                );
            })}
        </Box>
    );
};

export default function MonthlyPurchaseOrders(props: MonthlyPurchaseOrdersProps) {
    const { t, i18n } = useTranslation();
    const firstRender = useRef(true);
    const [data, setData] = useState<MonthlyPurchaseOrdersData | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [expandedSupplierId, setExpandedSupplierId] = useState<number | null>(null);
    const { control, formState: { errors }, setValue, watch } = useForm<{ [key: string]: any }>({
        defaultValues: {
            range: { value: 'monthly', label: t('mtd') },
        },
    });
    const filters = useMemo(() => [
        { value: 'daily', label: t('today') },
        { value: 'yesterday', label: t('yesterday') },
        { value: 'weekly', label: t('wtd') },
        { value: 'monthly', label: t('mtd') },
        { value: 'last_month', label: t('last_month') },
        { value: 'yearly', label: t('ytd') },
    ], [i18n.language]);
    const values = watch(['storeId', 'range']);
    const selectedStoreValue = Number(values[0]?.value ?? values[0]);
    const isAllStoreSelected = selectedStoreValue === 0 || props.globalFilters.allStore === true;
    const { refetch: apiRefetch } = useGetMonthlyPurchaseOrdersService({
        storeId: isAllStoreSelected ? null : (values[0]?.value || null),
        allStore: isAllStoreSelected,
        range: values[1]?.value || null,
    });
    const compactSelectSx = {
        '& .css-7896aj-control, & .css-1xiok1u-control': {
            minHeight: 'auto',
            fontSize: '9px',
            height: 'auto',
            overflow: 'hidden',
            '& .css-yzr845-indicatorContainer, & .css-1rr3n57-indicatorContainer, & .css-1asjhht-indicatorContainer, & .css-5s13ue-indicatorContainer': {
                background: '#fff',
                padding: 0,
                width: '15px',
                height: '15px',
            },
            '& .css-pogzpp-loadingIndicator, & .css-o9ehiq-loadingIndicator': { position: 'absolute', right: 0 },
            border: 0,
        },
    };
    useEffect(() => {
        if (firstRender.current && props.storesLoaded === true) {
            firstRender.current = false;
            const defaultStore = (props?.stores?.length > 1 ? props?.stores?.[1] : props?.stores?.[0]) || null;
            setValue('storeId', defaultStore);
            setValue('range', props.globalFilters.range);
            setTimeout(() => fetchData(), 50);
        }
    }, [props.stores, props.storesLoaded]);
    useEffect(() => {
        const currentRange = watch('range');
        const updated = filters.find(f => f.value === currentRange?.value);
        if (updated) setValue('range', updated);
    }, [i18n.language, filters]);
    useEffect(() => {
        if (props.globalFilterApplied === true) {
            setValue('storeId', props.globalFilters.storeId);
            setValue('range', props.globalFilters.range);
            setTimeout(() => fetchData(), 100);
        }
    }, [props.globalFilterApplied]);
    const fetchData = async () => {
        setLoading(true);
        setExpandedSupplierId(null);
        await apiRefetch().then((res: GetMonthlyPurchaseOrdersResponse | unknown | any) => {
            if (res?.data?.success) {
                const payload = res?.data?.data ?? res?.data;
                setData({
                    title: payload?.title,
                    totalPurchaseOrders: payload?.totalPurchaseOrders ?? 0,
                    suppliers: payload?.suppliers ?? [],
                });
            } else {
                props.enqueueSnackbar(<AlertMessages text={res?.data?.message || "Something went wrong"} />, { variant: "error" });
            }
        }).catch((err) => {
            console.error("===", err);
        }).finally(() => {
            setLoading(false);
            if (props.globalFilterApplied === true) props.setGlobalFilterApplied(false);
        });
    };
    const suppliers = useMemo(
        () => (data?.suppliers || []).filter((supplier) => {
            const breakdown = supplier.statusBreakdown ?? { billed: 0, inTransit: 0, pending: 0, received: 0 };
            return getActivePoCount(breakdown) > 0;
        }),
        [data?.suppliers],
    );
    const activeTotalPurchaseOrders = useMemo(
        () => suppliers.reduce((sum, supplier) => {
            const breakdown = supplier.statusBreakdown ?? { billed: 0, inTransit: 0, pending: 0, received: 0 };
            return sum + getActivePoCount(breakdown);
        }, 0),
        [suppliers],
    );
    const toggleSupplier = (supplierId: number) => {
        setExpandedSupplierId((current) => (current === supplierId ? null : supplierId));
    };
    return (
        <Card sx={{ height: '285px', boxShadow: 'none', borderRadius: '8px', border: '1px solid #E5E7EB', '& .MuiCardContent-root': { padding: '16px !important' } }}>
            <CardContent>
                <Box sx={dashboardWidgetHeaderSx}>
                    <Box sx={dashboardWidgetTitleRowSx}>
                    <Typography variant="h1" gutterBottom className="font-Saira text-start mb-0 mt-[5px]" sx={{ color: props.themes?.primary, fontSize: { xs: 12, sm: 14 }, fontWeight: { xs: 600, sm: 600 }, textAlign: "left" }}>
                        {t('Purchase Orders')}
                    </Typography>
</Box>
<Box sx={{ ...dashboardWidgetFiltersSx, flexDirection: "column", alignItems: { xs: "flex-start", sm: "flex-end" } }}>
                        <Box sx={dashboardWidgetFiltersSx}>
                            <Controller
                                name="storeId"
                                control={control}
                                defaultValue={null}
                                rules={{ required: t("This field is required.") }}
                                render={({ field }) => (
                                    <Box sx={compactSelectSx}>
                                        <DynamicSelect
                                            label={''}
                                            {...field}
                                            options={props?.stores || []}
                                            isDisabled={loading}
                                            onChange={(selectedOption: any) => {
                                                field.onChange(selectedOption);
                                                setTimeout(() => fetchData(), 50);
                                            }}
                                            error={errors.storeId?.message as string}
                                            ddWidth="30px"
                                            ddHeight="30px"
                                            defaultPlaceholder={t('store')}
                                            isLoading={props.searchLoading === 'monthlyPurchaseOrders'}
                                            onInputChange={(search: string, { action }: { action: string }) => {
                                                if (action === 'input-change' ||
                                                    (action !== 'set-value' && action !== 'menu-close' && action !== 'input-blur')
                                                ) props.handleDropdownActions("monthlyPurchaseOrders", 'searchIt', search);
                                                else if (action === 'input-blur' || action === 'menu-close') {
                                                    if (props.dropdownSearchTerm || props.dropdownPage > 1) props.handleDropdownActions("monthlyPurchaseOrders", 'searchIt', '');
                                                    else props.setDropdownSearchTerm('');
                                                    props.setDropdownPage(1);
                                                }
                                            }}
                                            onMenuScrollToBottom={() => { if (!(props.storesData?.pages[0]?.data as any)?.last) props.handleDropdownActions("monthlyPurchaseOrders", 'loadNext'); }}
                                            page={props.dropdownPage}
                                            isSearchable
                                        />
                                    </Box>
                                )}
                            />
                            <Controller
                                name="range"
                                control={control}
                                defaultValue={null}
                                rules={{ required: t("This field is required.") }}
                                render={({ field }) => (
                                    <Box sx={compactSelectSx}>
                                        <DynamicSelect
                                            label={''}
                                            {...field}
                                            options={filters}
                                            onChange={(selectedOption: any) => {
                                                field.onChange(selectedOption);
                                                setTimeout(() => fetchData(), 50);
                                            }}
                                            error={errors.range?.message as string}
                                            ddWidth="30px"
                                            ddHeight="30px"
                                            isDisabled={loading}
                                        />
                                    </Box>
                                )}
                            />
                        </Box>
                    </Box>
                </Box>
                {loading ? (
                    <>
                        <Skeleton animation="pulse" sx={{ backgroundColor: props?.themes?.secondary, marginTop: '5px' }} />
                        <Skeleton animation="pulse" sx={{ backgroundColor: props?.themes?.secondary }} />
                        <Skeleton animation="pulse" sx={{ backgroundColor: props?.themes?.secondary }} />
                        <Skeleton animation="pulse" sx={{ backgroundColor: props?.themes?.secondary }} />
                        <Skeleton animation="pulse" sx={{ backgroundColor: props?.themes?.secondary }} />
                        <Skeleton animation="pulse" sx={{ backgroundColor: props?.themes?.secondary }} />
                    </>
                ) : (
                    <Box>
                        {!loading && data ? (<Box className="flex items-center justify-between gap-1 mb-[10px]">
                            <Box className="flex flex-wrap items-center gap-2">
                                {STATUS_SEGMENTS.map(({ key, labelKey, color }) => (
                                    <Box key={key} className="flex items-center gap-1">
                                        <Box sx={{ width: 10, height: 10, borderRadius: '2px', backgroundColor: color }} />
                                        <Typography className="font-Saira text-[10px] text-[#6B7280]">{t(labelKey)}</Typography>
                                    </Box>
                                ))}
                            </Box>
                            <Typography className="font-Saira text-[11px] text-[#6B7280] whitespace-nowrap">
                                {activeTotalPurchaseOrders} {t('total').toLowerCase()}
                            </Typography>
                        </Box>) : null}
                        {(suppliers.length === 0 || !data) ? (
                            <Box className="flex justify-center flex-col">
                                <Typography className="font-Saira text-center italic font-semibold mt-[67px]" sx={{ color: props.themes.primary }}>
                                    {t('noRecordFound')}
                                </Typography>
                            </Box>
                        ) : (
                            <Box className="max-h-[190px] overflow-y-auto pr-[6px] flex flex-col gap-2">
                                {suppliers.map((item) => {
                            const isExpanded = expandedSupplierId === item.supplierId;
                            const breakdown = item.statusBreakdown ?? { billed: 0, inTransit: 0, pending: 0, received: 0 };
                            const activePoCount = getActivePoCount(breakdown);
                            const purchaseOrderDetails = (item.purchaseOrderDetail || []).filter(
                                (po) => po.status !== 'Pending',
                            );
                            const hasDetails = purchaseOrderDetails.length > 0;
                            return (
                                <Box
                                    key={item.supplierId}
                                    sx={{
                                        border: '1px solid #E5E7EB',
                                        borderRadius: '8px',
                                        padding: '12px',
                                        backgroundColor: '#fff',
                                    }}
                                >
                                    <Box
                                        className="flex items-center justify-between gap-2 cursor-pointer"
                                        onClick={() => hasDetails && toggleSupplier(item.supplierId)}
                                        role={hasDetails ? 'button' : undefined}
                                        tabIndex={hasDetails ? 0 : undefined}
                                        onKeyDown={(event) => {
                                            if (hasDetails && (event.key === 'Enter' || event.key === ' ')) {
                                                event.preventDefault();
                                                toggleSupplier(item.supplierId);
                                            }
                                        }}
                                    >
                                        <Box sx={dashboardWidgetTitleRowSx}>
                                            <StorefrontOutlinedIcon sx={{ fontSize: 15, color: '#9CA3AF' }} />
                                            <Tooltip followCursor title={item.supplierName}>
                                                <Typography className="font-Saira text-[12px] font-semibold text-[#171826] truncate">
                                                    {item.supplierName}
                                                </Typography>
                                            </Tooltip>
                                        </Box>
                                        <Box className="flex items-center gap-1 shrink-0">
                                            <Typography className="font-Saira text-[11px] text-[#6B7280]">
                                                {activePoCount} {t('pos')}
                                            </Typography>
                                            {hasDetails ? (
                                                isExpanded
                                                    ? <KeyboardArrowUpIcon sx={{ fontSize: 17, color: '#9CA3AF' }} />
                                                    : <KeyboardArrowDownIcon sx={{ fontSize: 17, color: '#9CA3AF' }} />
                                            ) : null}
                                        </Box>
                                    </Box>
                                    <Box className="mt-3 mb-2">
                                        <StatusProgressBar breakdown={breakdown} total={activePoCount} />
                                    </Box>
                                    <Typography className="font-Saira text-[10px] text-[#9CA3AF]">
                                        {formatStatusBreakdown(breakdown, t)}
                                    </Typography>
                                    <Collapse in={isExpanded}>
                                        <Box className="mt-3 pt-3 flex flex-col gap-2" sx={{ borderTop: '1px solid #F3F4F6' }}>
                                            {purchaseOrderDetails.map((po, index) => {
                                                const statusStyle = getPoDetailStatusStyle(po.status);
                                                return (
                                                    <Box key={`${po.poNumber}-${index}`} className="flex items-center justify-between gap-2">
                                                        <Box className="min-w-0">
                                                            <Typography className="font-Saira text-[11px] font-medium text-[#171826]">
                                                                {po.poNumber}
                                                            </Typography>
                                                            <Typography className="font-Saira text-[10px] text-[#9CA3AF]">
                                                                {formatPoDate(po.date)}
                                                            </Typography>
                                                        </Box>
                                                        <Box className="flex items-center gap-2 shrink-0">
                                                            <Typography className="font-Saira text-[11px] font-medium text-[#171826]">
                                                                {new Intl.NumberFormat('en', { maximumFractionDigits: 2 }).format(po.amount)}
                                                            </Typography>
                                                            <Box
                                                                className="px-2 py-0.5 rounded text-[9px] font-Saira font-medium"
                                                                sx={{ backgroundColor: statusStyle.backgroundColor, color: statusStyle.color }}
                                                            >
                                                                {po.status}
                                                            </Box>
                                                        </Box>
                                                    </Box>
                                                );
                                            })}
                                        </Box>
                                    </Collapse>
                                </Box>
                            );
                                })}
                            </Box>
                        )}
                    </Box>
                )}
            </CardContent>
        </Card>
    );
}
